/**
 * Fix & Flip solution page.
 */
import { Hammer, DollarSign, Target, LineChart, Building2, Brain } from "lucide-react";
import SolutionPageTemplate from "../SolutionPageTemplate";

export default function FixAndFlipSolutionPage() {
  return (
    <SolutionPageTemplate
      slug="fix-and-flip"
      persona="rehab-and-resell investors"
      eyebrow="Solutions · Fix &amp; Flip"
      title="Fund it. Fix it."
      titleAccent="Sell it — profitably."
      subtitle="Every rehab priced against real comps, not gut feel. Every ARV cited with sold data. Every exit compared — because sometimes a wholetail beats a full flip."
      bullets={[
        "Repair estimator with room-by-room evidence (BP-003)",
        "ARV comps with distance, DOM, and adjustment audit trail (BP-004)",
        "Flip vs Wholetail vs BRRRR scored side-by-side per property (BP-014)",
      ]}
      metricStrip={[
        { stat: "$31k", label: "avg. repair estimate" },
        { stat: "±6%", label: "ARV vs actual sale" },
        { stat: "4", label: "exit strategies compared" },
        { stat: "18d", label: "days-on-market target" },
      ]}
      pains={[
        { title: "Repair budgets blow up", desc: "\"We didn't see that mold behind the drywall.\" Underestimating repairs kills the deal margin." },
        { title: "ARV was optimistic", desc: "You priced against comps that weren't really comps. Real appraisal comes in $28k under." },
        { title: "Held too long", desc: "6-month flip becomes 11 months. Holding costs eat the margin you thought you had." },
        { title: "Wrong exit chosen", desc: "You flipped when a wholetail would have paid faster and cleaner — but you never scored the alternatives." },
      ]}
      playbook={[
        { step: 1, title: "Estimate repairs", desc: "Room-by-room + system-by-system with local labour rates.", bp: "BP-003" },
        { step: 2, title: "Pull comps", desc: "Sold-only comps with distance, DOM, and adjustment evidence.", bp: "BP-004" },
        { step: 3, title: "Score exits", desc: "Flip vs Wholetail vs BRRRR — capital, time, and risk-adjusted.", bp: "BP-014" },
        { step: 4, title: "Execute & track", desc: "Every draw, every contractor, every sale — timeline-audited.", bp: "BP-002 · BP-023" },
      ]}
      productsUsed={[
        { icon: Hammer, title: "Property Workspace", to: "/products/property-workspace", desc: "Repairs, contractor comms, draws, photos — all in the property command center." },
        { icon: DollarSign, title: "Strategy Comparison", to: "/products/strategy-comparison", desc: "Never guess — score Flip vs Wholetail vs BRRRR side-by-side per property." },
        { icon: Target, title: "AI Acquisition Coach", to: "/products/ai-acquisition-coach", desc: "Confidence-capped recommendations — no invented numbers on capital-heavy deals." },
        { icon: LineChart, title: "Market Intelligence", to: "/products/market-intelligence", desc: "Only flip in ZIPs where DOM is compressing — spot cooling markets early." },
        { icon: Brain, title: "Portfolio Intelligence", to: "/products/portfolio-intelligence", desc: "Track active flips as portfolio — cost, timeline, and margin per project." },
        { icon: Building2, title: "Executive Dashboard", to: "/products/executive-dashboard", desc: "Days-on-market, repair-budget accuracy, ARV variance in one view." },
      ]}
      proof={{
        stat: "6%",
        label: "avg. ARV variance vs actual sale",
        quote: "We stopped over-improving. The strategy comparison caught two properties where a wholetail was $18k more profitable than a full flip.",
        attribution: "Flip operator · 14 projects / year",
      }}
      related={[
        { title: "BRRRR", to: "/solutions/brrrr", desc: "Buy, Rehab, Rent, Refi, Repeat — with the same rehab engine." },
        { title: "Wholesalers", to: "/solutions/wholesalers", desc: "For when a flip becomes a fast assignment." },
        { title: "Buy & Hold", to: "/solutions/buy-and-hold", desc: "Turn a flip into a rental with the same underwriting." },
      ]}
    />
  );
}
